import { CoordinationSummary, SolutionState } from 'app/model/SOL49';
import AuthorisationUtils from 'app/common/context/AuthorisationUtils';
import { notification } from 'antd';
import { ModalHandler } from 'app/components/panels/ModalHandler';
import { Role } from 'app/model/CORE';
import Utils from 'app/common/util/Utils';
import { CoordinationStatusType } from '../../model/client/SOL49';
import { SolutionUtils } from './SolutionUtils';

// For each solution, for each coordination state, store the list of FMPs
export type SolutionsStatusMap = { [solName: string]: { [state: string]: string[] } };

export class CoordinationUtils {
    static DEFAULT_COORDINATION_DURATION = 30;

    static isNotAcknowledged = (coordinationStatus: CoordinationStatusType): boolean => {
        // NM or Dev user doesn't need to ack the coordination
        if (AuthorisationUtils.isNmUser()) return false;

        const fmpSolutions: { [p: string]: SolutionState } = coordinationStatus[AuthorisationUtils.getFmpUnit()];
        return Object.values(fmpSolutions).some(state => state === SolutionState.NO_RESPONSE);
    };

    static getCoordinationOrigin = (summary: CoordinationSummary) => {
        return summary.solutions.some(SolutionUtils.isInitialSolution) ? Role.FMP : Role.NM;
    };

    static onOpenCoordination = (coordination: CoordinationSummary, onCoordinationUpdate: (Coordination) => void) => {
        notification.close(coordination.id);
        ModalHandler.showCoordinationPanel(coordination.id, onCoordinationUpdate);
    };

    static getCoordinationStatusMap = (coordination: CoordinationSummary) => {
        const result: { [solName: string]: { [state: string]: string[] } } = {};
        coordination.solutions.map(s => {
            const fmpsPerStateMap: { [state: string]: string[] } = {};
            Object.keys(SolutionState).forEach(state => (fmpsPerStateMap[state] = []));

            for (const fmp of Object.keys(coordination.status)) {
                const state = coordination.status[fmp][s.name];
                fmpsPerStateMap[state].push(fmp);
            }
            result[s.name] = fmpsPerStateMap;
        });
        return result;
    };

    static getCoordinationStateUpdateDiff = (
        coordination: CoordinationSummary,
        cache: SolutionsStatusMap,
    ): SolutionsStatusMap => {
        const diff: SolutionsStatusMap = {};
        const newStatusMap = CoordinationUtils.getCoordinationStatusMap(coordination);
        coordination.solutions.forEach(solution => {
            Object.entries(newStatusMap[solution.name]).forEach(([state, fmps]) => {
                const previousFmps = (cache && cache[solution.name][state]) || [];
                // Ignore updates made by current fmp
                fmps = fmps.filter(fmp => fmp !== AuthorisationUtils.getFmpUnit());
                if (
                    state !== SolutionState.ACKNOWLEDGED &&
                    fmps.length >= previousFmps.length &&
                    Utils.isNotShallowEquals(fmps, previousFmps)
                ) {
                    if (Utils.isNotExists(diff[solution.name])) {
                        diff[solution.name] = {};
                    }
                    diff[solution.name][state] = fmps;
                }
            });
        });
        return diff;
    };

    static toSolutionStateShortString = (state: SolutionState): string => {
        switch (state) {
            case SolutionState.NO_RESPONSE:
                return '???';
            case SolutionState.ACKNOWLEDGED:
                return 'ACK';
            case SolutionState.APPROVED:
                return 'OK';
            case SolutionState.REJECTED:
                return 'NOK';
            case SolutionState.TIMED_OUT:
                return 'TO';
        }
    };
}
